import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { C } from "./reportingUtils";

/**
 * WalkMe-style guided tour: dims the screen, spotlights the actual on-page
 * element for each step, and anchors an explanation tooltip to it.
 *
 * steps: [{ selector, title, text, onEnter?() }]
 *   selector — CSS selector of the real element to highlight
 *   onEnter  — optional side effect when the step opens (e.g. switch tab)
 */
const TIP_W = 330;
const PAD = 8;

export default function SpotlightTour({ open, steps, onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps && steps[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    try { el.scrollIntoView({ block: "center", behavior: "smooth", inline: "nearest" }); } catch { /* ignore */ }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => { if (open) setI(0); }, [open]);

  // Run the step's side effect, then measure (after content settles).
  useEffect(() => {
    if (!open || !step) return;
    step.onEnter && step.onEnter();
    const t1 = setTimeout(measure, 80);
    const t2 = setTimeout(measure, 320); // re-measure after any smooth scroll
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open, i, step, measure]);

  useEffect(() => {
    if (!open) return;
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [open, measure]);

  if (!open || !step) return null;

  const spot = rect
    ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
    : null;

  // Position the tooltip: below the spotlight if there's room, else above; keep on-screen.
  let tipTop, tipLeft;
  if (spot) {
    const below = spot.top + spot.height + 12;
    const roomBelow = below + 200 < window.innerHeight;
    tipTop = roomBelow ? below : Math.max(12, spot.top - 200);
    tipLeft = Math.min(Math.max(12, spot.left), window.innerWidth - TIP_W - 12);
  } else {
    tipTop = window.innerHeight / 2 - 100;
    tipLeft = window.innerWidth / 2 - TIP_W / 2;
  }

  const last = i === steps.length - 1;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 2000 }}>
      {/* click blocker (keeps focus on the tour) */}
      <Box onClick={(e) => e.stopPropagation()} sx={{ position: "fixed", inset: 0 }} />

      {/* dark overlay with a bright cutout around the target */}
      {spot ? (
        <Box sx={{
          position: "fixed", top: spot.top, left: spot.left, width: spot.width, height: spot.height,
          borderRadius: "10px", boxShadow: "0 0 0 9999px rgba(12,24,48,.68)",
          border: "2px solid #FFD34E", pointerEvents: "none",
          transition: "top .25s ease, left .25s ease, width .25s ease, height .25s ease",
        }} />
      ) : (
        <Box sx={{ position: "fixed", inset: 0, background: "rgba(12,24,48,.68)" }} />
      )}

      {/* explanation anchored to the element */}
      <Box sx={{
        position: "fixed", top: tipTop, left: tipLeft, width: TIP_W, background: "#fff",
        borderRadius: "14px", boxShadow: "0 20px 55px rgba(0,0,0,.4)", p: 2.25,
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.blue, mb: 0.5 }}>
          Walk me through it · {i + 1} of {steps.length}
        </Typography>
        <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: C.ink, mb: 0.75 }}>{step.title}</Typography>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: C.slate, mb: 2 }}>{step.text}</Typography>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Button onClick={onClose} sx={{ textTransform: "none", color: C.muted, fontSize: 12.5, minWidth: 0 }}>Skip tour</Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            {i > 0 && (
              <Button onClick={() => setI((s) => Math.max(0, s - 1))} sx={{ textTransform: "none", color: C.ink }}>Back</Button>
            )}
            <Button variant="contained" onClick={() => (last ? onClose() : setI((s) => s + 1))}
              sx={{ textTransform: "none", fontWeight: 700, background: C.blue, borderRadius: "9px", px: 2.75, "&:hover": { background: "#2559c4" } }}>
              {last ? "Done" : "Next"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
